import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { ApiToken } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'

import { useApiTokensApi } from '../../api/api-tokens/list.hook'
import { useApiTokensStore } from './store'

interface TableApiTokensReturn {
  tokens: ComputedRef<ApiToken[]>
  isLoading: ComputedRef<boolean>
  hasTokens: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  formatDateTime: (value: string | null) => string
  getUserName: (token: ApiToken) => string
  getUserHref: (token: ApiToken) => string
  isExpired: (token: ApiToken) => boolean
  openEditModal: (token: ApiToken) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableApiTokens = (): TableApiTokensReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useApiTokensStore()
  const dayjs = useDayjs()

  const tokens = computed(() => store.getTokens)
  const isLoading = computed(() => store.isLoading)
  const hasTokens = computed(() => store.hasTokens)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getUserName = (token: ApiToken): string => token.user?.name ?? '—'

  const getUserHref = (token: ApiToken): string =>
    token.user ? (useAdminRoutes('admin.user.show', { user_id: token.user.id }) ?? '') : ''

  const isExpired = (token: ApiToken): boolean =>
    token.expires_at !== null && dayjs(token.expires_at).isBefore(dayjs())

  const openEditModal = (token: ApiToken): void => {
    store.setTokenToEdit(token)
  }

  const setChangePage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useApiTokensApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await useApiTokensApi()
  }

  return {
    tokens,
    isLoading,
    hasTokens,
    meta,
    formatDateTime,
    getUserName,
    getUserHref,
    isExpired,
    openEditModal,
    setChangePage,
    setChangePerPage,
  }
}
